import { isEqual } from "lodash";
import type { Query } from "react-query";

import { detailKeyRoot } from "./constants";

interface PageWithNextPage {
  has_next_page: boolean;
  page: number;
}

export const joinBatchIds = (ids: number[]) =>
  [...ids].sort((left, right) => left - right).join(",");

export const getCurrentPageParam = ({ pageParam }: { pageParam?: unknown }) =>
  typeof pageParam === "number" ? pageParam : 1;

export const getNextPageParam = <TPage extends PageWithNextPage>(
  lastPage: TPage,
) => (lastPage.has_next_page ? lastPage.page + 1 : undefined);

// Details data is stored using slug identifier in cache. Iterate on ID to find target item.
const matchPostDetailsQuery =
  (postId: number) =>
  ({ queryKey, state }: Query): boolean => {
    if (!isEqual(queryKey.slice(0, 3), detailKeyRoot("record"))) return false;

    return (
      !!state.data &&
      typeof state.data === "object" &&
      "id" in state.data &&
      state.data.id === postId
    );
  };

// Details are stored in batches of serialized string IDs. Match IDs to find target item.
export const matchPostDetailsBatchQuery =
  (postId: number) =>
  ({ queryKey }: Query): boolean => {
    if (!isEqual(queryKey.slice(0, 2), detailKeyRoot())) return false;

    const batchName = queryKey[2];
    if (batchName !== "batch" && batchName !== "shared-batch") return false;

    const params = queryKey[3];
    if (!params || typeof params !== "object") return false;
    if (!("post_ids" in params) || typeof params.post_ids !== "string")
      return false;

    return `,${params.post_ids},`.includes(`,${postId},`);
  };

// A post's effective detail surface spans both the record query and the
// batch/shared-batch queries. Match either so callers can invalidate one post's
// detail without knowing the split.
export const matchPostDetailQuery = (postId: number) => {
  const matchRecord = matchPostDetailsQuery(postId);
  const matchBatch = matchPostDetailsBatchQuery(postId);

  return (query: Query): boolean => matchRecord(query) || matchBatch(query);
};
